import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, PlayCircle, AlertCircle, UserPlus } from "lucide-react";
import { Assignee } from "./AssigneeAvatarGroup";

interface AssignmentOverallStatusProps {
  assignees: Assignee[];
  variant?: "badge" | "dot" | "text";
  size?: "small" | "medium" | "large";
}

export function AssignmentOverallStatus({
  assignees,
  variant = "badge",
  size = "medium",
}: AssignmentOverallStatusProps) {
  if (!assignees || assignees.length === 0) {
    const config = {
      label: "Unassigned",
      icon: UserPlus,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
    };

    if (variant === "dot") {
      return (
        <div className="flex items-center gap-1">
          <div className={`h-2 w-2 rounded-full ${config.bgColor}`} />
          <span className={`text-xs ${config.color}`}>{config.label}</span>
        </div>
      );
    }

    if (variant === "text") {
      const Icon = config.icon;
      return (
        <div className="flex items-center gap-1">
          <Icon className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{config.label}</span>
        </div>
      );
    }

    return (
      <Badge variant="outline" className="gap-1 text-muted-foreground border-dashed">
        <config.icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  }

  // Calculate overall status
  const allCompleted = assignees.every((a) => a.assignment_status === "completed");
  const anyBlocked = assignees.some((a) => a.assignment_status === "blocked");
  const anyInProgress = assignees.some((a) => a.assignment_status === "in_progress");
  const allPending = assignees.every((a) => a.assignment_status === "pending");

  let config: {
    label: string;
    icon: any;
    color: string;
    bgColor: string;
    borderColor?: string;
  };

  if (allCompleted) {
    config = {
      label: "All Complete",
      icon: CheckCircle2,
      color: "text-green-700",
      bgColor: "bg-green-100",
      borderColor: "border-green-600",
    };
  } else if (anyBlocked) {
    config = {
      label: "Blocked",
      icon: AlertCircle,
      color: "text-red-700",
      bgColor: "bg-red-100",
      borderColor: "border-red-600",
    };
  } else if (anyInProgress) {
    config = {
      label: "In Progress",
      icon: PlayCircle,
      color: "text-blue-700",
      bgColor: "bg-blue-100",
      borderColor: "border-blue-600",
    };
  } else if (allPending) {
    config = {
      label: "Pending",
      icon: Clock,
      color: "text-yellow-700",
      bgColor: "bg-yellow-100",
      borderColor: "border-yellow-600",
    };
  } else {
    config = {
      label: "Mixed",
      icon: PlayCircle,
      color: "text-blue-700",
      bgColor: "bg-blue-100",
      borderColor: "border-blue-600",
    };
  }

  const Icon = config.icon;
  const iconSize = size === "small" ? "h-3 w-3" : size === "large" ? "h-5 w-5" : "h-4 w-4";

  if (variant === "dot") {
    return (
      <div className="flex items-center gap-1">
        <div className={`h-2 w-2 rounded-full ${config.bgColor}`} />
        <span className={`text-xs ${config.color}`}>{config.label}</span>
      </div>
    );
  }

  if (variant === "text") {
    return (
      <div className="flex items-center gap-1">
        <Icon className={`${iconSize} ${config.color}`} />
        <span className={`text-xs ${config.color}`}>{config.label}</span>
      </div>
    );
  }

  return (
    <Badge
      variant="outline"
      className={`gap-1 ${config.color} ${config.bgColor}`}
      style={{ borderColor: config.borderColor }}
    >
      <Icon className={iconSize} />
      {config.label}
    </Badge>
  );
}
